/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Backoffice API Gateway
 * OpenAPI spec version: 1.0.0
 */
import type {
  AccountCommunication,
  AccountCommunicationEmail,
  AccountDetailsResponse,
  AccountFinderRequestV2,
  AccountFinderResponseV2,
  BillRequest,
  CancelAccountRequest,
  CancelAccountResponse,
  CreateAccountCreditRequest,
  CreateRefundApprovalRequest,
  EditAccountReviewPeriodRequest,
  GetAccountCommunicationsParams,
  GetAccountDetailsByNumberParams,
  PostSmartPayAsYouGoParams,
  SmartPayAsYouGoRequest,
  UpdatePaperBill,
} from "../model"

import { backofficeRequest } from "../../client"

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1]

/**
 * @summary Cancel an account
 */
export const cancelAccountRegistration = (
  billingAccountId: number,
  cancelAccountRequest: CancelAccountRequest,
  options?: SecondParameter<typeof backofficeRequest<CancelAccountResponse>>,
) => {
  return backofficeRequest<CancelAccountResponse>(
    {
      url: `/accounts/${billingAccountId}/cancel`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: cancelAccountRequest,
    },
    options,
  )
}
/**
 * @summary Returns the details of an account filtered by external junifer account number
 */
export const getAccountDetailsByNumber = (
  params: GetAccountDetailsByNumberParams,
  options?: SecondParameter<typeof backofficeRequest<AccountDetailsResponse>>,
) => {
  return backofficeRequest<AccountDetailsResponse>(
    { url: `/accounts/details`, method: "GET", params },
    options,
  )
}
/**
 * @summary Edits an account review period by removing the current review period and creating a new one
 */
export const editAccountReviewPeriod = (
  billingAccountId: number,
  editAccountReviewPeriodRequest: EditAccountReviewPeriodRequest,
  options?: SecondParameter<typeof backofficeRequest<void>>,
) => {
  return backofficeRequest<void>(
    {
      url: `/accounts/${billingAccountId}/editAccountReviewPeriod`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: editAccountReviewPeriodRequest,
    },
    options,
  )
}
/**
 * Enables or disables the standard paper-bill secondary contact (`2ndclass@so.energy`) on a billing account. The primary contact's `billDelivery` always stays `Email`; postal delivery is determined solely by whether this secondary is active.
 *
 * **Intended scope:**
 * - Use only for non-PSR-qualifying customers. PSR customers may have `LARGE_PRINT` or `BRAILLE` secondaries — route those changes through the PSR flow instead.
 * - This endpoint manages **standard** paper bills only. It cannot enable specialist formats (large print, braille).
 *
 * **Built-in guards** — the call returns 204 with no Junifer mutation and no audit note when:
 * - the primary contact's `lastName == "Occupier"`, or
 * - the account already has an active `LARGE_PRINT` (`large.print@so.energy`) or `BRAILLE` (`braille.print@so.energy`) secondary contact.
 * @summary Toggle standard paper bills for an account
 */
export const updatePaperBill = (
  billingAccountId: number,
  updatePaperBillBody: UpdatePaperBill,
  options?: SecondParameter<typeof backofficeRequest<void>>,
) => {
  return backofficeRequest<void>(
    {
      url: `/accounts/${billingAccountId}/paper-bill`,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data: updatePaperBillBody,
    },
    options,
  )
}
/**
 * @summary Submits refund request for the given account
 */
export const submitRefundApprovalRequest = (
  billingAccountId: number,
  createRefundApprovalRequest: CreateRefundApprovalRequest,
  options?: SecondParameter<typeof backofficeRequest<void>>,
) => {
  return backofficeRequest<void>(
    {
      url: `/account/${billingAccountId}/request-refund`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: createRefundApprovalRequest,
    },
    options,
  )
}
/**
 * @summary register for Smart Pay As You Go
 */
export const postSmartPayAsYouGo = (
  smartPayAsYouGoRequest: SmartPayAsYouGoRequest,
  params: PostSmartPayAsYouGoParams,
  options?: SecondParameter<typeof backofficeRequest<void>>,
) => {
  return backofficeRequest<void>(
    {
      url: `/accounts/postSmartPayAsYouGo`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: smartPayAsYouGoRequest,
      params,
    },
    options,
  )
}
/**
 * @summary Get bill requests for a billing account
 */
export const getAccountBillRequests = (
  billingAccountId: number,
  options?: SecondParameter<typeof backofficeRequest<BillRequest[]>>,
) => {
  return backofficeRequest<BillRequest[]>(
    {
      url: `/api/v1/accounts/${billingAccountId}/bill_requests`,
      method: "GET",
    },
    options,
  )
}
/**
 * Retrieve all communications for a specific account
 * @summary Get account communications
 */
export const getAccountCommunications = (
  id: number,
  params?: GetAccountCommunicationsParams,
  options?: SecondParameter<typeof backofficeRequest<AccountCommunication[]>>,
) => {
  return backofficeRequest<AccountCommunication[]>(
    { url: `/accounts/${id}/communications`, method: "GET", params },
    options,
  )
}
/**
 * The email Junifer sent for a communication on this billing account. 204 when the communication is letter-only (no email); open its files instead.
 * @summary Get the email sent for a communication
 */
export const getAccountCommunicationEmail = (
  billingAccountId: number,
  communicationId: number,
  options?: SecondParameter<
    typeof backofficeRequest<AccountCommunicationEmail | void>
  >,
) => {
  return backofficeRequest<AccountCommunicationEmail | void>(
    {
      url: `/accounts/${billingAccountId}/communications/${communicationId}/email`,
      method: "GET",
    },
    options,
  )
}
/**
 * A file (the PDF the customer received) of a communication on this billing account.
 * @summary Get a communication file
 */
export const getAccountCommunicationFile = (
  billingAccountId: number,
  communicationId: number,
  fileId: number,
  options?: SecondParameter<typeof backofficeRequest<Blob>>,
) => {
  return backofficeRequest<Blob>(
    {
      url: `/accounts/${billingAccountId}/communications/${communicationId}/files/${fileId}`,
      method: "GET",
      responseType: "blob",
    },
    options,
  )
}
/**
 * Asks Junifer to open the account in the logged-in agent's Junifer UI; the agent is taken from the JWT, not the request. Only has a visible effect for agents on Windows on the So network with Junifer already open (never via the Mac VM). Junifer does not report a missing UI, so a 204 means Junifer accepted the request, not that anything opened. Relies on Junifer's openUi endpoint, which is absent from current Junifer docs and may be withdrawn.
 * @summary Opens the account in the calling agent's Junifer desktop UI
 */
export const openJuniferUi = (
  billingAccountId: number,
  options?: SecondParameter<typeof backofficeRequest<void>>,
) => {
  return backofficeRequest<void>(
    { url: `/accounts/${billingAccountId}/open-junifer-ui`, method: "POST" },
    options,
  )
}
/**
 * @summary Create account credit
 */
export const createAccountCredit = (
  createAccountCreditRequest: CreateAccountCreditRequest,
  options?: SecondParameter<typeof backofficeRequest<void>>,
) => {
  return backofficeRequest<void>(
    {
      url: `/account/createAccountCredit`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: createAccountCreditRequest,
    },
    options,
  )
}
/**
 * @summary Account Finder V2
 */
export const findAccountV2 = (
  accountFinderRequestV2: AccountFinderRequestV2,
  options?: SecondParameter<typeof backofficeRequest<AccountFinderResponseV2>>,
) => {
  return backofficeRequest<AccountFinderResponseV2>(
    {
      url: `/v2/account-finder`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: accountFinderRequestV2,
    },
    options,
  )
}
export type CancelAccountRegistrationResult = NonNullable<
  Awaited<ReturnType<typeof cancelAccountRegistration>>
>
export type GetAccountDetailsByNumberResult = NonNullable<
  Awaited<ReturnType<typeof getAccountDetailsByNumber>>
>
export type EditAccountReviewPeriodResult = NonNullable<
  Awaited<ReturnType<typeof editAccountReviewPeriod>>
>
export type UpdatePaperBillResult = NonNullable<
  Awaited<ReturnType<typeof updatePaperBill>>
>
export type SubmitRefundApprovalRequestResult = NonNullable<
  Awaited<ReturnType<typeof submitRefundApprovalRequest>>
>
export type PostSmartPayAsYouGoResult = NonNullable<
  Awaited<ReturnType<typeof postSmartPayAsYouGo>>
>
export type GetAccountBillRequestsResult = NonNullable<
  Awaited<ReturnType<typeof getAccountBillRequests>>
>
export type GetAccountCommunicationsResult = NonNullable<
  Awaited<ReturnType<typeof getAccountCommunications>>
>
export type GetAccountCommunicationEmailResult = NonNullable<
  Awaited<ReturnType<typeof getAccountCommunicationEmail>>
>
export type GetAccountCommunicationFileResult = NonNullable<
  Awaited<ReturnType<typeof getAccountCommunicationFile>>
>
export type OpenJuniferUiResult = NonNullable<
  Awaited<ReturnType<typeof openJuniferUi>>
>
export type CreateAccountCreditResult = NonNullable<
  Awaited<ReturnType<typeof createAccountCredit>>
>
export type FindAccountV2Result = NonNullable<
  Awaited<ReturnType<typeof findAccountV2>>
>
